import { Component, inject } from '@angular/core';
import { ComponentsModule, DialogService, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { createAddressFormGroup } from '@lib/api/models/address';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { CommissionerService } from '@lib/commissioner/commissioner.service';
import { Commissioner, CommissionerSingleResponse, createCommissionerFormGroup, StoreCommissionerRequest } from '@lib/api/models/commissioner';
import { NgForOf } from '@angular/common';
import { createContactNumberFormGroup, StoreContactNumberRequest } from '@lib/api/models/contact-numbers';

@Component({
    selector: 'app-edit-commissioner-contact-address-dialog',
    imports: [
        ErrorPanelComponent,
        ReactiveFormsModule,
        NgForOf,
        ComponentsModule
    ],
    templateUrl: './edit-commissioner-contact-address-dialog.component.html',
    standalone: true,
    styleUrl: './edit-commissioner-contact-address-dialog.component.scss'
})
export class EditCommissionerContactAddressDialogComponent {

    private readonly _commissionerService = inject(CommissionerService);
    private readonly _snackBar = inject(SnackBarService);

    protected readonly commissioner = inject<Commissioner>(DIALOG_DATA);
    protected readonly commissionerDetailFormGroup = createCommissionerFormGroup(this.commissioner);
    protected readonly addressFormGroup = createAddressFormGroup(this.commissioner.address);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    contactNumbersForm: FormGroup;

    updateCommissionerContactNumberRequest = new SendableRequest<CommissionerSingleResponse, StoreCommissionerRequest>({
        request: payload => this._commissionerService.updateCommissioner(this.commissioner.id, payload!),

        onSuccess: () => {
            return;
        }
    });

    updateCommissionerRequest = new SendableRequest<CommissionerSingleResponse, StoreCommissionerRequest>({
        request: (payload) => this._commissionerService.updateCommissioner(this.commissioner.id, payload!),

        onSend: () => {
            this.commissionerDetailFormGroup.disable();
            this.addressFormGroup.disable();
        },

        onSuccess: response => {
            this.commissionerDetailFormGroup.enable();
            this.addressFormGroup.enable();

            let contactNumbers = this.contactNumbersForm.value.contactNumbers;

            if (contactNumbers.length === 1) {
                if (contactNumbers[0].number === '' && contactNumbers[0].type === null) {
                    contactNumbers = [];
                }
            }

            this.addContactNumberToCommissioner(response.data, contactNumbers);

            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Contact & Property Updated'
            });
            this.dialogRef.close();
        },

        onError: () => {
            this.commissionerDetailFormGroup.enable();
            this.addressFormGroup.enable();
        }
    });

    constructor(private fb: FormBuilder) {
        this.contactNumbersForm = this.fb.group({
            contactNumbers: this.fb.array(
                this.commissioner.contact_numbers.map((contact) => createContactNumberFormGroup(contact))
            )
        });
    }

    get contactNumbersFormArray(): FormArray {
        return this.contactNumbersForm.get('contactNumbers') as FormArray;
    }

    addContactNumberToCommissioner(commissioner: Commissioner, contactNumbers: StoreContactNumberRequest[]) {
        const commissionerData: StoreCommissionerRequest = {
            reference: commissioner.reference,
            name: commissioner.name,
            email: commissioner.email,
            contact_numbers_to_store: contactNumbers
        };

        this.updateCommissionerContactNumberRequest.send(commissionerData);
    }

    addContactNumber() {
        this.contactNumbersFormArray.push(createContactNumberFormGroup());
    }

    removeContactNumber(index: number) {
        this.contactNumbersFormArray.removeAt(index);

        if (this.contactNumbersFormArray.length === 0) {
            this.addContactNumber();
        }
    }

    update() {
        if (this.updateCommissionerRequest.loading()) {
            return;
        }

        const commissionerData: StoreCommissionerRequest = {
            reference: this.commissioner.reference,
            name: this.commissioner.name,
            email: this.commissionerDetailFormGroup.get('email')?.value ?? '',
            address: {
                street_1: this.addressFormGroup.get('street_1')?.value ?? '',
                street_2: this.addressFormGroup.get('street_2')?.value ?? '',
                town: this.addressFormGroup.get('town')?.value ?? '',
                county: this.addressFormGroup.get('county')?.value ?? '',
                country: this.addressFormGroup.get('country')?.value ?? '',
                post_code: this.addressFormGroup.get('post_code')?.value ?? '',
                hard_to_reach: this.addressFormGroup.get('hard_to_reach')?.value ?? false,
                additional_comments: this.addressFormGroup.get('additional_comments')?.value ?? '',
                lat: this.addressFormGroup.get('lat')?.value ?? 0,
                lng: this.addressFormGroup.get('lng')?.value ?? 0
            }
        };


        this.updateCommissionerRequest.send(commissionerData);
    }

    public static show(dialog: DialogService, data: Commissioner) {
        return dialog.openDialog<DialogRef, Commissioner>(EditCommissionerContactAddressDialogComponent, {
            data
        });
    }
}
